import { useCallback } from 'react';
import { getReceiptVoucherColumns } from '../cols-definition';
import type { PhieuThuSearchFilters } from '../types';
import type { PhieuThu } from '@/types/schemas';
import { SearchFormValues } from '../schema';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: PhieuThuSearchFilters, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: SearchFormValues;
  setSearchParams: (params: SearchFormValues) => void;
  handleViewClick: (obj: PhieuThu) => void;
  sumTotal: number | null;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({
  searchData,
  searchParams,
  setSearchParams,
  handleViewClick,
  sumTotal,
  isFiltered,
  handlePageChange
}: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: PhieuThuSearchFilters) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: PhieuThuSearchFilters, onSearchFn: (params: PhieuThuSearchFilters) => Promise<void>) => {
      const { dynamic_filters, ...searchParamsOnly } = formData;
      setSearchParams(searchParamsOnly as SearchFormValues);
      await onSearchFn(formData);
    },
    [setSearchParams]
  );

  const getColumns = useCallback(() => {
    const cols = getReceiptVoucherColumns(handleViewClick, sumTotal);
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, [handleViewClick, sumTotal]);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.PHIEU_THU}/filter`,
    pageSize: 20,
    sumField: 't_tien'
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData(searchParams, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchParams, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
